<script setup>
import { ref, computed } from 'vue';
import { useI18n } from 'vue-i18n';
import Dialog from 'dashboard/components-next/dialog/Dialog.vue';
import SquaremeAPI from 'dashboard/api/squareme';
import { messageTimestamp } from 'shared/helpers/timeHelper';
import {
  ESCALATION_TYPES,
  actionTypeKey,
  formatMoney,
  hasSubject,
} from 'dashboard/helper/squareme';

const props = defineProps({
  conversationId: { type: [Number, String], required: true },
  customer: { type: Object, required: true },
  allowedTypes: { type: Array, default: () => [] },
  // How long to wait between checks for the proposal's outcome.
  outcomeCheckMs: { type: Number, default: 1000 },
});

const emit = defineEmits(['created', 'refused']);

const OUTCOME_CHECKS = 8;

const { t } = useI18n();

const dialogRef = ref(null);
const actionType = ref('');
const targetId = ref('');
const choice = ref('');
const reason = ref('');
const items = ref([]);
const itemsLoading = ref(false);
const itemsError = ref(false);
const submitting = ref(false);
const error = ref('');
// An item picked from the activity list that may be older than the options fetched here.
const presetItem = ref(null);

const spec = computed(() => ESCALATION_TYPES[actionType.value] || null);

const typeOptions = computed(() =>
  props.allowedTypes
    .filter(type => ESCALATION_TYPES[type])
    .map(type => ({
      value: type,
      label: t(`SQUAREME_PANEL.ACTION_TYPES.${actionTypeKey(type)}`),
    }))
);

const profile = computed(() => props.customer.profile || {});

const submissionOptions = computed(() =>
  (props.customer.kyc?.submissions?.latest || []).map(submission => ({
    value: submission.id,
    label: t('SQUAREME_PANEL.DIALOG.SUBMISSION_OPTION', {
      tier: submission.tier,
      idType: submission.idType,
      status: submission.status,
      date: submission.at
        ? messageTimestamp(Date.parse(submission.at) / 1000)
        : '',
    }),
  }))
);

const itemLabel = item =>
  [
    item.title,
    formatMoney(item.amount),
    messageTimestamp(Date.parse(item.createdAt) / 1000),
    t(`SQUAREME_PANEL.ACTIVITY.STATUS.${item.status}`),
  ].join(' · ');

const itemOptions = computed(() => {
  const options = items.value
    .filter(hasSubject)
    .map(item => ({ value: item.reference, label: itemLabel(item) }));
  const preset = presetItem.value;
  if (preset && !options.some(option => option.value === preset.reference)) {
    options.unshift({ value: preset.reference, label: itemLabel(preset) });
  }
  return options;
});

const loadItems = async () => {
  items.value = [];
  itemsError.value = false;
  if (spec.value?.source !== 'activity') return;
  itemsLoading.value = true;
  try {
    const { data } = await SquaremeAPI.activity(props.conversationId, {
      type: spec.value.itemTypes.join(','),
      limit: 50,
    });
    items.value = data.transactions || [];
  } catch {
    itemsError.value = true;
  } finally {
    itemsLoading.value = false;
  }
};

const onTypeChange = () => {
  targetId.value = spec.value?.source === 'customer' ? profile.value.id : '';
  choice.value = '';
  error.value = '';
  loadItems();
};

const trimmedReason = computed(() => reason.value.trim());

const isValid = computed(
  () =>
    !!spec.value &&
    !!targetId.value &&
    (!spec.value.choice || !!choice.value) &&
    trimmedReason.value.length >= 5 &&
    trimmedReason.value.length <= 500
);

const payload = computed(() => {
  const body = { [spec.value.idField]: targetId.value };
  if (spec.value.choice) body[spec.value.choice.field] = choice.value;
  return body;
});

const describeError = e => {
  const data = e?.response?.data || {};
  if (e?.response?.status === 422 && data.messages?.length) {
    return t('SQUAREME_PANEL.DIALOG.INVALID', {
      messages: data.messages.join('; '),
    });
  }
  return t('SQUAREME_PANEL.DIALOG.UNAVAILABLE');
};

const wait = ms =>
  new Promise(resolve => {
    setTimeout(resolve, ms);
  });

// Relay sends the proposal in the background. Wait briefly for its outcome,
// so a target Watchtower cannot act on is explained here, on the form.
const settle = async escalation => {
  let current = escalation;
  for (let i = 0; i < OUTCOME_CHECKS && current.status === 'pending'; i += 1) {
    // eslint-disable-next-line no-await-in-loop
    await wait(props.outcomeCheckMs);
    try {
      // eslint-disable-next-line no-await-in-loop
      const { data } = await SquaremeAPI.escalations(props.conversationId);
      current =
        (data.escalations || []).find(item => item.id === escalation.id) ||
        current;
    } catch {
      return current;
    }
  }
  return current;
};

const submit = async () => {
  if (!isValid.value || submitting.value) return;
  submitting.value = true;
  error.value = '';
  try {
    const { data } = await SquaremeAPI.escalate(props.conversationId, {
      actionType: actionType.value,
      payload: payload.value,
      reason: trimmedReason.value,
    });
    const escalation = await settle(data);
    if (escalation.status === 'refused') {
      error.value = t('SQUAREME_PANEL.DIALOG.REFUSED', {
        message: escalation.last_error,
      });
      emit('refused', escalation);
      return;
    }
    emit('created', escalation);
    dialogRef.value?.close();
  } catch (e) {
    error.value = describeError(e);
  } finally {
    submitting.value = false;
  }
};

// preset: { actionType, payload, item } from an activity item, or nothing.
const open = async (preset = {}) => {
  presetItem.value = preset.item || null;
  reason.value = '';
  error.value = '';
  actionType.value = preset.actionType || '';
  onTypeChange();
  if (preset.actionType && preset.payload) {
    targetId.value =
      preset.payload[ESCALATION_TYPES[preset.actionType].idField] || '';
  }
  dialogRef.value?.open();
};

defineExpose({ open });
</script>

<template>
  <Dialog
    ref="dialogRef"
    :title="$t('SQUAREME_PANEL.DIALOG.TITLE')"
    :description="$t('SQUAREME_PANEL.DIALOG.DESCRIPTION')"
    :confirm-button-label="$t('SQUAREME_PANEL.DIALOG.SUBMIT')"
    :disable-confirm-button="!isValid"
    :is-loading="submitting"
    overflow-y-auto
    @confirm="submit"
  >
    <div class="flex flex-col gap-4 text-sm text-n-slate-12">
      <label class="flex flex-col gap-1">
        <span class="font-medium">{{ $t('SQUAREME_PANEL.DIALOG.TYPE') }}</span>
        <select
          v-model="actionType"
          data-test-id="squareme-escalation-type"
          class="w-full !mb-0 rounded-lg border border-n-weak bg-n-surface-1 px-3 py-2 text-sm"
          @change="onTypeChange"
        >
          <option value="" disabled>
            {{ $t('SQUAREME_PANEL.DIALOG.TYPE_PLACEHOLDER') }}
          </option>
          <option
            v-for="option in typeOptions"
            :key="option.value"
            :value="option.value"
          >
            {{ option.label }}
          </option>
        </select>
      </label>

      <div v-if="spec?.source === 'customer'" class="flex flex-col gap-1">
        <span class="font-medium">{{
          $t('SQUAREME_PANEL.DIALOG.CUSTOMER')
        }}</span>
        <p
          data-test-id="squareme-escalation-customer"
          class="mb-0 rounded-lg bg-n-slate-2 px-3 py-2"
        >
          {{ profile.name || $t('SQUAREME_PANEL.NO_NAME') }}
          <span v-if="profile.phone" class="text-n-slate-11">
            · {{ profile.phone }}
          </span>
        </p>
      </div>

      <label v-else-if="spec?.source === 'kyc'" class="flex flex-col gap-1">
        <span class="font-medium">
          {{ $t('SQUAREME_PANEL.DIALOG.SUBMISSION') }}
        </span>
        <select
          v-if="submissionOptions.length"
          v-model="targetId"
          data-test-id="squareme-escalation-target"
          class="w-full !mb-0 rounded-lg border border-n-weak bg-n-surface-1 px-3 py-2 text-sm"
        >
          <option value="" disabled>
            {{ $t('SQUAREME_PANEL.DIALOG.SUBMISSION_PLACEHOLDER') }}
          </option>
          <option
            v-for="option in submissionOptions"
            :key="option.value"
            :value="option.value"
          >
            {{ option.label }}
          </option>
        </select>
        <span v-else class="text-n-slate-11">
          {{ $t('SQUAREME_PANEL.DIALOG.NO_SUBMISSIONS') }}
        </span>
      </label>

      <label
        v-else-if="spec?.source === 'activity'"
        class="flex flex-col gap-1"
      >
        <span class="font-medium">{{ $t('SQUAREME_PANEL.DIALOG.ITEM') }}</span>
        <span v-if="itemsLoading" class="text-n-slate-11">
          {{ $t('SQUAREME_PANEL.LOADING') }}
        </span>
        <span v-else-if="itemsError" class="text-n-ruby-11">
          {{ $t('SQUAREME_PANEL.DIALOG.ITEMS_ERROR') }}
        </span>
        <select
          v-else-if="itemOptions.length"
          v-model="targetId"
          data-test-id="squareme-escalation-target"
          class="w-full !mb-0 rounded-lg border border-n-weak bg-n-surface-1 px-3 py-2 text-sm"
        >
          <option value="" disabled>
            {{ $t('SQUAREME_PANEL.DIALOG.ITEM_PLACEHOLDER') }}
          </option>
          <option
            v-for="option in itemOptions"
            :key="option.value"
            :value="option.value"
          >
            {{ option.label }}
          </option>
        </select>
        <span v-else class="text-n-slate-11">
          {{ $t('SQUAREME_PANEL.DIALOG.NO_ITEMS') }}
        </span>
      </label>

      <fieldset v-if="spec?.choice" class="flex flex-col gap-1">
        <legend class="mb-1 font-medium">
          {{
            spec.choice.field === 'decision'
              ? $t('SQUAREME_PANEL.DIALOG.CHOICE_DECISION')
              : $t('SQUAREME_PANEL.DIALOG.CHOICE_OUTCOME')
          }}
        </legend>
        <label
          v-for="option in spec.choice.options"
          :key="option"
          class="flex items-center gap-2"
        >
          <input
            v-model="choice"
            type="radio"
            :value="option"
            :data-test-id="`squareme-escalation-choice-${option}`"
            class="!mb-0"
          />
          {{ $t(`SQUAREME_PANEL.CHOICES.${option}`) }}
        </label>
      </fieldset>

      <label v-if="spec" class="flex flex-col gap-1">
        <span class="font-medium">{{
          $t('SQUAREME_PANEL.DIALOG.REASON')
        }}</span>
        <textarea
          v-model="reason"
          rows="3"
          maxlength="500"
          data-test-id="squareme-escalation-reason"
          :placeholder="$t('SQUAREME_PANEL.DIALOG.REASON_PLACEHOLDER')"
          class="w-full !mb-0 rounded-lg border border-n-weak bg-n-surface-1 p-3 text-sm"
        />
        <span class="text-xs text-n-slate-11">
          {{
            $t('SQUAREME_PANEL.DIALOG.REASON_HINT', {
              count: trimmedReason.length,
            })
          }}
        </span>
      </label>

      <p
        v-if="error"
        role="alert"
        data-test-id="squareme-escalation-error"
        class="mb-0 rounded-lg bg-n-ruby-3 px-3 py-2 text-n-ruby-11"
      >
        {{ error }}
      </p>
    </div>
  </Dialog>
</template>
